import type { CSSProperties } from 'react';
import { gray200, gray500, gray700, inputBackground, mutedForeground, white } from './tokens';

/**
 * Styled after the Make prototype's My Notes card: the editing surface mimics its
 * shadcn Textarea (light gray fill, transparent border, 8px radius, 160px min height).
 * JS-only styles — the embed bundle is a single self-contained ESM file, so CSS files
 * are off limits.
 */
export const styles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	/** Sizing/padding live on the contenteditable itself (see editorProps in RichTextEditor). */
	editor: {
		wordWrap: 'break-word',
	},
	editorSurface: {
		background: inputBackground,
		border: '1px solid transparent',
		borderRadius: 8,
		fontSize: 14,
		overflowY: 'auto',
		position: 'relative',
	},
	/** URL field in the Link popover; shrinks with the popover so a narrow host still fits it. */
	linkInput: {
		background: white,
		border: `1px solid ${gray200}`,
		borderRadius: 6,
		color: gray700,
		flex: '1 1 140px',
		fontSize: 13,
		minWidth: 0,
		outline: 'none',
		padding: '5px 8px',
	},
	/**
	 * Link popover: a row (field + actions) rather than the stacked list the dropdowns use. Capped
	 * against the viewport because this editor also ships inside the Outlook task pane (~320px).
	 */
	linkMenu: {
		alignItems: 'center',
		background: white,
		border: `1px solid ${gray200}`,
		borderRadius: 8,
		boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
		boxSizing: 'border-box',
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
		marginTop: 2,
		maxWidth: 'min(320px, 90vw)',
		padding: 6,
		position: 'absolute',
		width: 'max-content',
		zIndex: 10,
	},
	linkMenuApply: {
		background: gray700,
		border: 'none',
		borderRadius: 6,
		color: white,
		cursor: 'pointer',
		fontSize: 13,
		padding: '5px 10px',
	},
	linkMenuRemove: {
		background: 'transparent',
		border: 'none',
		borderRadius: 6,
		color: gray500,
		cursor: 'pointer',
		fontSize: 13,
		padding: '5px 8px',
	},
	menu: {
		background: white,
		border: `1px solid ${gray200}`,
		borderRadius: 8,
		boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
		display: 'flex',
		flexDirection: 'column',
		marginTop: 2,
		minWidth: 140,
		padding: 4,
		position: 'absolute',
		zIndex: 10,
	},
	/** Dropdown entry: full-width, left-aligned icon + label. */
	menuItem: {
		alignItems: 'center',
		background: 'transparent',
		border: 'none',
		borderRadius: 6,
		color: gray700,
		cursor: 'pointer',
		display: 'flex',
		fontSize: 13,
		gap: 8,
		padding: '6px 8px',
		textAlign: 'left',
		whiteSpace: 'nowrap',
		width: '100%',
	},
	menuWrapper: {
		position: 'relative',
	},
	/** Stand-in for the Textarea placeholder; pseudo-elements need CSS we cannot ship. */
	placeholder: {
		color: mutedForeground,
		fontSize: 14,
		left: 12,
		pointerEvents: 'none',
		position: 'absolute',
		top: 8,
	},
	toolbar: {
		alignItems: 'center',
		borderBottom: `1px solid ${gray200}`,
		display: 'flex',
		flexWrap: 'wrap',
		gap: 2,
		paddingBottom: 6,
	},
	/** Icon button: square-ish tap target, icon inherits color via currentColor. */
	toolbarButton: {
		alignItems: 'center',
		background: 'transparent',
		border: 'none',
		borderRadius: 6,
		color: gray700,
		cursor: 'pointer',
		display: 'flex',
		gap: 2,
		padding: 6,
	},
	toolbarButtonActive: {
		background: gray200,
		color: gray700,
	},
	/** Vertical rule separating toolbar groups, mirroring the web app's grouping. */
	toolbarDivider: {
		alignSelf: 'center',
		background: gray200,
		height: 20,
		margin: '0 4px',
		width: 1,
	},
} satisfies Record<string, CSSProperties>;
